import { FC } from "react";

import { cn } from "helpers/classNames";
import { NvRapporteringStatus } from "enums/NvRapporteringStatus.enum";
import {
  CheckCircleIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

interface Props {
  status: NvRapporteringStatus;
  className?: string;
  isButton?: boolean;
}

const containerBaseClass =
  "inline-flex items-center gap-x-0.5 rounded-md md:pl-1.5 pl-2.5 pr-3 md:pr-2 py-1 text-sm md:text-xs font-medium ring-1 ring-inset";

const iconBaseClass = "group relative ml-0.5 h-3.5 w-auto rounded-sm sm:h-4";

export const NvRapporteringStatusTag: FC<Props> = ({
  status,
  className,
  isButton = false,
}) => (
  <span
    className={cn(
      containerBaseClass,
      isButton && "shadow-sm hover:shadow-md",
      getStatusTagContainerClass(status),
      className,
    )}
  >
    {getUppdragStatusLabel(status)}
    {status === NvRapporteringStatus.Completed && (
      <CheckCircleIcon className="group relative ml-0.5 h-3.5 w-auto rounded-sm text-green-600 hover:bg-green-600/20 sm:h-4" />
    )}
  </span>
);

export const getStatusTagContainerClass = (status: NvRapporteringStatus) => {
  switch (status) {
    case NvRapporteringStatus.WaitingForReport:
      return "bg-yellow-50 text-yellow-900 ring-yellow-600/20";
    case NvRapporteringStatus.NotReported:
      return "bg-stone-50 text-stone-900 ring-stone-600/20";

    case NvRapporteringStatus.HasReportedPreliminaryWeight:
      return "bg-blue-50 text-blue-900 ring-blue-600/30";
    case NvRapporteringStatus.AutomaticReportFailed:
      return "bg-red-50 text-red-900 ring-red-600/30";
    case NvRapporteringStatus.Completed:
      return "bg-green-50 text-green-900 ring-green-600/40";
    case NvRapporteringStatus.NotApplicable:
    case NvRapporteringStatus.Ignored:
      return "bg-gray-25 text-gray-700 ring-gray-500/20";
  }
};

export const NvRapporteringStatusIcon = ({
  status,
}: {
  status: NvRapporteringStatus;
}) => {
  switch (status) {
    case NvRapporteringStatus.Completed:
      return (
        <CheckCircleIcon
          className={cn(iconBaseClass, "text-green-600 hover:bg-green-600/20")}
        />
      );
    case NvRapporteringStatus.HasReportedPreliminaryWeight:
      return (
        <CheckBadgeIcon
          className={cn(iconBaseClass, "text-pink-600 hover:bg-pink-600/20")}
        />
      );
    case NvRapporteringStatus.AutomaticReportFailed:
      return (
        <ExclamationTriangleIcon
          className={cn(iconBaseClass, "text-red-600 hover:bg-red-600/20")}
        />
      );
    default:
      return null;
  }
};

// export const UppdragStatusIcon: FC<Props> = ({
//   status,
//   className,
//   disabled,
// }) => (
//   <>
//     {status === UppdragStatus.Scheduled ? (
//       <CalendarDaysIcon
//         className={cn("text-gray-400", iconBaseClass, className)}
//         aria-hidden="true"
//       />
//     ) : status === UppdragStatus.Started ? (
//       <ClipboardIcon
//         className={cn("text-blue-400", iconBaseClass, className)}
//         aria-hidden="true"
//       />
//     ) : status === UppdragStatus.Completed ? (
//       <CheckCircleIcon
//         className={cn(
//           "text-cyan-500",
//           iconBaseClass,
//           className,
//           disabled && "text-gray-300",
//         )}
//         aria-hidden="true"
//       />
//     ) : (
//       "missing"
//     )}
//   </>
// );

export const getUppdragStatusLabel = (status: NvRapporteringStatus) => {
  switch (status) {
    case NvRapporteringStatus.AutomaticReportFailed:
      return "Misslyckades";
    case NvRapporteringStatus.HasReportedPreliminaryWeight:
      return "Preliminärt rapporterad";
    case NvRapporteringStatus.Completed:
      return "Rapporterad";
    case NvRapporteringStatus.Ignored:
      return "Ignorerad";
    case NvRapporteringStatus.NotApplicable:
      return "Inte tillämpligt";
    case NvRapporteringStatus.NotReported:
      return "Ej rapporterad";
    case NvRapporteringStatus.WaitingForReport:
      return "Redo att rapporteras";
  }
};
